import { useState, useEffect, useCallback } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "react-router-dom";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { tradespurpleOnboardingSchema, TradesPurpleOnboardingValues } from "@/lib/schemas";
import { updateProfile, isUsernameAvailable } from "@/lib/supabase/profiles";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import TradesPurpleLayout from "@/components/Layout";
import { debug } from "@/lib/utils";
import { Helmet } from "react-helmet-async";
import { TradePersonSelectionStep } from "@/components/onboarding/TradePersonSelectionStep";

export default function TradesPurpleOnboardingPage() {
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckingUsername, setIsCheckingUsername] = useState(false);
  const [currentStep, setCurrentStep] = useState<'profile' | 'selection'>('profile');
  const [postcodeValue, setPostcodeValue] = useState('');
  const navigate = useNavigate();
  const { user } = useAuth();

  // Initialize form at component top level to avoid rule of hooks violation
  const form = useForm<TradesPurpleOnboardingValues>({
    resolver: zodResolver(tradespurpleOnboardingSchema),
    defaultValues: {
      username: "",
      postcode: "",
    },
    mode: "onChange"
  });

  const watchUsername = form.watch("username");
  const watchPostcode = form.watch("postcode");

  // Check username availability when it changes
  const checkUsername = useCallback(async (username: string) => {
    if (!username || username.length < 3) return;

    try {
      setIsCheckingUsername(true);
      const isAvailable = await isUsernameAvailable(username);

      if (!isAvailable) {
        form.setError("username", {
          type: "manual",
          message: "This username is already taken"
        });
      } else {
        form.clearErrors("username");
      }
    } catch (error) {
      debug.error("Error checking username:", error);
    } finally {
      setIsCheckingUsername(false);
    }
  }, [form, setIsCheckingUsername]);

  // Debounce username checks using useEffect properly
  useEffect(() => {
    if (watchUsername && watchUsername.length >= 3) {
      const timeoutId = setTimeout(() => {
        checkUsername(watchUsername);
      }, 500);

      return () => clearTimeout(timeoutId);
    }
  }, [watchUsername, checkUsername]);

  // Redirect if no user
  if (!user) {
    navigate("/auth/login");
    return null;
  }

  const onSubmit = async (data: TradesPurpleOnboardingValues) => {
    if (!user) return;

    setIsLoading(true);
    try {
      // Final username availability check
      const isAvailable = await isUsernameAvailable(data.username);
      if (!isAvailable) {
        form.setError("username", {
          type: "manual",
          message: "This username is already taken"
        });
        setIsLoading(false);
        return;
      }

      // Update profile with username and postcode
      await updateProfile(user.id, {
        username: data.username,
        postcode: data.postcode.toUpperCase(),
      });

      toast.success("Profile created successfully!");

      // Store postcode for use in the tradesperson selection step
      setPostcodeValue(data.postcode.toUpperCase());

      // Move to the tradesperson selection step
      setCurrentStep('selection');
      setIsLoading(false);
    } catch (error) {
      toast.error("Failed to create profile");
      debug.error("Onboarding error:", error);
      setIsLoading(false);
    }
  };

  // Handle completion of the tradesperson selection step
  const handleSelectionComplete = () => {
    // Navigate to root landing page (which shows LoggedInLandingPage for authenticated users)
    navigate('/'); // Root URL will show logged-in landing page
  };

  return (
    <TradesPurpleLayout>
      <Helmet>
        <title>
          {currentStep === 'profile'
            ? 'TradesPurple | Complete Your Profile'
            : 'TradesPurple | Add Local Tradespeople'}
        </title>
        <meta
          name="description"
          content={currentStep === 'profile'
            ? "Set up your TradesPurple profile to save and share trusted tradespeople."
            : "Add local tradespeople to your TradesPurple list."
          }
        />
      </Helmet>

      <div className="flex items-center justify-center mt-5">
        {currentStep === 'profile' ? (
          <Card className="w-full border-2 border-purple-900 rounded-xl shadow-lg">
            <CardHeader className="space-y-1">
              <CardTitle className="text-2xl font-bold text-center" style={{ color: "#5D4A9C" }}>Complete Your Profile</CardTitle>
              <CardDescription className="text-center text-purple-700">
                Choose a username and enter your postcode to get started
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                  <FormField
                    control={form.control}
                    name="username"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel style={{ color: "#5D4A9C" }}>Username</FormLabel>
                        <FormControl>
                          <div className="relative">
                            <div className="flex">
                              <span className="inline-flex items-center px-3 rounded-l-md border-2 border-r-0 border-purple-300 bg-purple-50 text-purple-700 text-sm">
                                tradespurple.com/
                              </span>
                              <Input
                                placeholder="your-username"
                                className="border-2 border-purple-300 focus:border-purple-500 rounded-l-none pr-12"
                                {...field}
                                disabled={isLoading}
                              />
                            </div>
                            {isCheckingUsername && (
                              <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                                <div className="h-4 w-4 border-2 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
                              </div>
                            )}
                          </div>
                        </FormControl>
                        <FormDescription className="text-sm text-purple-600">
                          This is the link for your public list
                        </FormDescription>
                        <FormMessage className="text-red-600" />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="postcode"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel style={{ color: "#5D4A9C" }}>Postal Area</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="Enter your postal code"
                            className="border-2 border-purple-300 focus:border-purple-500"
                            {...field}
                            disabled={isLoading}
                            onChange={(e) => {
                              // Convert to uppercase and limit to 8 characters for full postal codes
                              const value = e.target.value.slice(0, 8).toUpperCase();
                              field.onChange(value);
                            }}
                            maxLength={8}
                          />
                        </FormControl>
                        <FormDescription className="text-sm text-purple-600">
                          <div className="flex items-center flex-wrap">
                            <span className="mr-2">Enter your area code, or just the first half from</span>
                            <span className="text-base">🇬🇧🇮🇪🇺🇸🇨🇦🇦🇺🇳🇱🇲🇹</span>
                          </div>
                        </FormDescription>
                        <FormMessage className="text-red-600" />
                      </FormItem>
                    )}
                  />
                  <Button
                    type="submit"
                    className="w-full bg-purple-700 hover:bg-purple-800"
                    disabled={isLoading || isCheckingUsername}
                  >
                    {isLoading ? "Creating profile..." : "Continue"}
                  </Button>
                </form>
              </Form>
            </CardContent>
          </Card>
        ) : (
          <TradePersonSelectionStep
            postcode={postcodeValue}
            onComplete={handleSelectionComplete}
            onSkip={handleSelectionComplete}
          />
        )}
      </div>
    </TradesPurpleLayout>
  );
}
